import {safeHref} from './safe-href'

const DEFAULT_ALLOWED_TAGS = new Set(['p', 'br', 'a', 'strong', 'em', 'ul', 'ol', 'li'])
const DEFAULT_ALLOWED_ATTRS = new Set(['href', 'target', 'rel'])
const VOID_TAGS = new Set(['br'])

type SanitizeHtmlOptions = {
  allowedTags?: string[]
  allowedAttrs?: string[]
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const sanitizeAttribute = (tagName: string, attrName: string, value: string) => {
  if (tagName !== 'a') return null

  if (attrName === 'href') {
    // Render the string `safeHref` returned, never the caller's original: the
    // URL parser strips TAB/LF/CR from anywhere in the input, so validating a
    // stripped copy and emitting the raw value re-opens the hole this closes.
    const href = safeHref(value)
    return href ? `href="${escapeHtml(href)}"` : null
  }

  if (attrName === 'target') {
    return value === '_blank' ? 'target="_blank"' : null
  }

  if (attrName === 'rel') {
    const rel = value
      .split(/\s+/)
      .filter((item) => ['nofollow', 'noopener', 'noreferrer'].includes(item))
      .join(' ')

    return rel ? `rel="${rel}"` : null
  }

  return null
}

export function sanitizeHtml(html: string, options: SanitizeHtmlOptions = {}) {
  const allowedTags = new Set(options.allowedTags ?? DEFAULT_ALLOWED_TAGS)
  const allowedAttrs = new Set(options.allowedAttrs ?? DEFAULT_ALLOWED_ATTRS)

  return html.replace(/<\/?([a-zA-Z][\w:-]*)([^>]*)>|([^<]+)/g, (match, rawTag, rawAttrs, text) => {
    if (text) return escapeHtml(text)

    const tagName = String(rawTag).toLowerCase()
    if (!allowedTags.has(tagName)) return ''

    const isClosingTag = match.startsWith('</')
    if (isClosingTag) return VOID_TAGS.has(tagName) ? '' : `</${tagName}>`

    const attrs: string[] = []
    String(rawAttrs).replace(
      /\s+([^\s=/>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g,
      (_attrMatch, rawName, doubleQuotedValue, singleQuotedValue, unquotedValue) => {
        const attrName = String(rawName).toLowerCase()
        if (!allowedAttrs.has(attrName)) return ''

        const attr = sanitizeAttribute(
          tagName,
          attrName,
          doubleQuotedValue ?? singleQuotedValue ?? unquotedValue ?? '',
        )
        if (attr) attrs.push(attr)
        return ''
      },
    )

    // Reverse tabnapping protection: whenever target="_blank" is present on an
    // <a> tag, ensure rel contains "noopener noreferrer" regardless of whether
    // the source HTML included a rel attribute. Without this, a CMS-authored
    // link like <a href="https://evil.com" target="_blank"> gives the opened
    // page access to window.opener, enabling it to redirect the originating
    // tab to a phishing page.
    if (tagName === 'a') {
      const hasBlankTarget = attrs.some((a) => a === 'target="_blank"')
      if (hasBlankTarget) {
        const relIndex = attrs.findIndex((a) => a.startsWith('rel="'))
        if (relIndex === -1) {
          attrs.push('rel="noopener noreferrer"')
        } else {
          // Merge with any existing rel values the input supplied
          const existingRel = attrs[relIndex].slice(5, -1) // strip rel=" and "
          const relParts = new Set(existingRel.split(/\s+/).filter(Boolean))
          relParts.add('noopener')
          relParts.add('noreferrer')
          attrs[relIndex] = `rel="${Array.from(relParts).join(' ')}"`
        }
      }
    }

    return `<${tagName}${attrs.length ? ` ${attrs.join(' ')}` : ''}>`
  })
}
